/**
 * Add a custom MCP server, or edit one's connection. "Connect" sends the
 * form to the workspace server, which checks the connection (initialize +
 * tools/list) before anything is stored; when that check fails its reason
 * shows here, under the form, and nothing is saved.
 *
 * The secret is a password field that starts empty every time. On edit a
 * blank secret keeps the stored one, as long as the kind of credential is
 * unchanged.
 *
 * @module components/apps/integrations/CustomMcpServerDialog
 */
import { Loader2Icon, PlugIcon } from "lucide-react";
import { useId, useState } from "react";

import type {
  CustomMcpResult,
  CustomMcpScope,
  CustomMcpServer,
} from "../../../state/customMcpServers";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Toggle, ToggleGroup } from "../../ui/toggle-group";
import {
  CUSTOM_MCP_AUTH_OPTIONS,
  CUSTOM_MCP_HEADER_NAME_MAX,
  CUSTOM_MCP_NAME_MAX,
  EMPTY_CUSTOM_MCP_FORM,
  customMcpFormFor,
  keepsStoredSecret,
  validateCustomMcpForm,
  type CustomMcpForm,
  type CustomMcpFormField,
} from "./customMcpServerContent";

export function CustomMcpServerDialog({
  open,
  onOpenChange,
  server = null,
  scopeOptions = DEFAULT_SCOPE_OPTIONS,
  onSubmit,
  onConnected,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The server whose connection is edited; null adds a new one. */
  readonly server?: CustomMcpServer | null;
  /** Who a new server may be for; the first is preselected. Ignored on edit. */
  readonly scopeOptions?: ReadonlyArray<ScopeOption>;
  readonly onSubmit: (form: CustomMcpForm) => Promise<CustomMcpResult>;
  /** After a successful check and save; the stored server when the answer carried it. */
  readonly onConnected?: (server: CustomMcpServer | null) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="sm:max-w-lg">
        {/* Mounted per opening, so a closed dialog forgets what was typed. */}
        <CustomMcpServerForm
          server={server}
          scopeOptions={scopeOptions}
          onCancel={() => onOpenChange(false)}
          onSubmit={onSubmit}
          onConnected={(stored) => {
            onOpenChange(false);
            onConnected?.(stored);
          }}
        />
      </DialogPopup>
    </Dialog>
  );
}

type ScopeOption = { readonly id: CustomMcpScope; readonly label: string };

const DEFAULT_SCOPE_OPTIONS: ReadonlyArray<ScopeOption> = [
  { id: "organization", label: "Everyone in the organization" },
];

export function CustomMcpServerForm({
  server,
  scopeOptions = DEFAULT_SCOPE_OPTIONS,
  onCancel,
  onSubmit,
  onConnected,
}: {
  readonly server: CustomMcpServer | null;
  readonly scopeOptions?: ReadonlyArray<ScopeOption>;
  readonly onCancel: () => void;
  readonly onSubmit: (form: CustomMcpForm) => Promise<CustomMcpResult>;
  readonly onConnected: (server: CustomMcpServer | null) => void;
}) {
  const id = useId();
  const [form, setForm] = useState<CustomMcpForm>(() =>
    server === null
      ? { ...EMPTY_CUSTOM_MCP_FORM, scope: scopeOptions[0]?.id ?? EMPTY_CUSTOM_MCP_FORM.scope }
      : customMcpFormFor(server),
  );
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState<{
    readonly field: CustomMcpFormField;
    readonly message: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const edit = (patch: Partial<CustomMcpForm>) => {
    setForm((current) => ({ ...current, ...patch }));
    setInvalid(null);
    setError(null);
  };

  const keepsSecret = keepsStoredSecret(server, form.authType);
  const authOption = CUSTOM_MCP_AUTH_OPTIONS.find((option) => option.id === form.authType);

  const submit = async () => {
    const problem = validateCustomMcpForm(form, server);
    if (problem !== null) {
      setInvalid(problem);
      return;
    }
    setBusy(true);
    setError(null);
    const outcome = await onSubmit(form);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    onConnected(outcome.server);
  };

  const fieldError = (field: CustomMcpFormField) =>
    invalid?.field === field ? (
      <span role="alert" className="text-xs text-destructive">
        {invalid.message}
      </span>
    ) : null;

  return (
    <form
      className="flex min-h-0 flex-col"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!busy) void submit();
      }}
    >
      <DialogHeader>
        <DialogTitle>{server === null ? "Add custom MCP server" : "Edit connection"}</DialogTitle>
        <DialogDescription>
          {server === null
            ? form.scope === "personal"
              ? "Connect a remote MCP server for yourself. Eve can use the tools you allow in your chats."
              : "Connect a remote MCP server for the whole organization. Eve can use the tools you allow."
            : "Amoreg checks the connection again and re-reads the server's tools."}
        </DialogDescription>
      </DialogHeader>
      <DialogPanel className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${id}-name`} className="text-sm font-medium text-foreground">
            Name
          </label>
          <Input
            id={`${id}-name`}
            required
            autoFocus
            autoComplete="off"
            maxLength={CUSTOM_MCP_NAME_MAX}
            placeholder="Internal knowledge base"
            disabled={busy}
            aria-invalid={invalid?.field === "name" || undefined}
            value={form.name}
            onChange={(event) => edit({ name: event.currentTarget.value })}
          />
          {fieldError("name")}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${id}-url`} className="text-sm font-medium text-foreground">
            Server URL
          </label>
          <Input
            id={`${id}-url`}
            type="url"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://mcp.example.com/mcp"
            className="font-mono text-xs"
            disabled={busy}
            aria-invalid={invalid?.field === "url" || undefined}
            value={form.url}
            onChange={(event) => edit({ url: event.currentTarget.value })}
          />
          {fieldError("url") ?? (
            <span className="text-xs text-muted-foreground">
              The server's streamable HTTP endpoint. Public https only.
            </span>
          )}
        </div>

        {server === null && scopeOptions.length > 1 ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">Who can use it</span>
            <ToggleGroup
              variant="outline"
              size="sm"
              aria-label="Who can use it"
              value={[form.scope]}
              disabled={busy}
              onValueChange={(next: ReadonlyArray<string>) => {
                const chosen = scopeOptions.find((option) => option.id === next[0]);
                if (chosen !== undefined && chosen.id !== form.scope) edit({ scope: chosen.id });
              }}
            >
              {scopeOptions.map((option) => (
                <Toggle
                  key={option.id}
                  value={option.id}
                  aria-label={option.label}
                  className="cursor-pointer px-3"
                >
                  {option.label}
                </Toggle>
              ))}
            </ToggleGroup>
            <span className="text-xs text-muted-foreground">
              {form.scope === "personal"
                ? "Only you see it, and Eve uses it only in chats you are driving."
                : "Everyone in the organization sees it; only admins can change it."}
            </span>
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">Authentication</span>
          <ToggleGroup
            variant="outline"
            size="sm"
            aria-label="Authentication"
            value={[form.authType]}
            disabled={busy}
            onValueChange={(next: ReadonlyArray<string>) => {
              const chosen = CUSTOM_MCP_AUTH_OPTIONS.find((option) => option.id === next[0]);
              if (chosen !== undefined && chosen.id !== form.authType) {
                edit({ authType: chosen.id, secret: "" });
              }
            }}
          >
            {CUSTOM_MCP_AUTH_OPTIONS.map((option) => (
              <Toggle
                key={option.id}
                value={option.id}
                aria-label={option.label}
                className="cursor-pointer px-3"
              >
                {option.label}
              </Toggle>
            ))}
          </ToggleGroup>
          <span className="text-xs text-muted-foreground">{authOption?.description}</span>
        </div>

        {form.authType === "header" ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-header`} className="text-sm font-medium text-foreground">
              Header name
            </label>
            <Input
              id={`${id}-header`}
              autoComplete="off"
              spellCheck={false}
              maxLength={CUSTOM_MCP_HEADER_NAME_MAX}
              placeholder="X-API-Key"
              className="font-mono text-xs"
              disabled={busy}
              aria-invalid={invalid?.field === "headerName" || undefined}
              value={form.headerName}
              onChange={(event) => edit({ headerName: event.currentTarget.value })}
            />
            {fieldError("headerName")}
          </div>
        ) : null}

        {form.authType !== "none" ? (
          <div className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1.5">
              <label htmlFor={`${id}-secret`} className="text-sm font-medium text-foreground">
                {form.authType === "bearer" ? "Token" : "Header value"}
              </label>
              {keepsSecret ? <span className="text-xs text-muted-foreground">optional</span> : null}
            </span>
            <Input
              id={`${id}-secret`}
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              placeholder={keepsSecret ? "Leave blank to keep the stored value" : undefined}
              disabled={busy}
              aria-invalid={invalid?.field === "secret" || undefined}
              value={form.secret}
              onChange={(event) => edit({ secret: event.currentTarget.value })}
            />
            {fieldError("secret") ?? (
              <span className="text-xs text-muted-foreground">
                Stored encrypted for the organization and never shown again.
              </span>
            )}
          </div>
        ) : null}

        {error !== null ? (
          <p
            role="alert"
            className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        ) : null}
      </DialogPanel>
      <DialogFooter>
        <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? <Loader2Icon className="size-4 animate-spin" /> : <PlugIcon className="size-4" />}
          {busy ? "Checking connection…" : server === null ? "Connect" : "Save and reconnect"}
        </Button>
      </DialogFooter>
    </form>
  );
}
